import React from "react";
import { withTranslation } from "react-i18next";
import * as api from "../api/api";
import { allRegionsId, maximumYear, minimumYear } from "../constants";
import {
    filterPopulationData,
    filterSurveyData,
    normalizeDatabases,
    normalizeEmuData,
    normalizePopulationData,
    normalizeSurveyData,
} from "../data";
import { withErrorBoundary } from "../errors";
import StartRun from "../pages/StartRun";

const DEBUG_BETA_STRING = process.env.REACT_APP_DEBUG_BETA_STRING;

const createInitialState = () => {
    return {
        surveyDatabaseType: "default",
        selectedSurveyDatabaseId: undefined,
        uploadedSurveyDatabaseFile: undefined,

        populationDatabaseType: "default",
        selectedPopulationDatabaseId: undefined,
        uploadedPopulationDatabaseFile: undefined,

        emuDatabaseType: "none",
        selectedEmuDatabaseId: undefined,
        uploadedEmuDatabaseFile: undefined,

        selectedRunType: "National", // ALl, National, Subnational

        selectedCountryNumericCode: undefined,
        selectedRegion: undefined,

        runName: "",

        period: {
            firstYear: minimumYear,
            lastYear: maximumYear,
        },

        // NOTE: OTHER STATE
        loadingData: true,
        databases: undefined,

        // Review Data
        loadingTables: false,
        surveyData: undefined,
        populationData: undefined,
        emuData: undefined,

        // Start run
        submittingRun: false,

        // Uploading
        uploadingSurveyDatabase: false,
        uploadingPopulationDatabase: false,
        uploadingEmuDatabase: false,

        // Uploading
        surveyDatabaseUploadProgress: undefined,
        populationDatabaseUploadProgress: undefined,
        emuDatabaseUploadProgress: undefined,

        // Notification
        notificationVariant: undefined,
        notificationOpen: false,
        notificationMessage: undefined,
    };
};

const removeFileExtension = (filename) => {
    return filename.replace(/\.[^/.]+$/, "");
};

const getDatabaseName = (file) => {
    return file && removeFileExtension(file.name);
};

const uploadDatabase = (file, databaseType, onProgress, signal) => {
    const data = new FormData();
    data.append("file", file, file.name);
    return api.uploadDatabase(data, databaseType, onProgress, signal.token);
};

const getCountryIntersection = (databases, surveyDatabaseId, populationDatabaseId, emuDatabaseId) => {
    if (!surveyDatabaseId || !populationDatabaseId) {
        return {};
    }

    const surveyDatabase = databases[surveyDatabaseId];
    const populationDatabase = databases[populationDatabaseId];
    const emuDatabase = emuDatabaseId && databases[emuDatabaseId];

    const countries = Object.keys(surveyDatabase.divisions).reduce((result, divisionNumericCode) => {
        const division = surveyDatabase.divisions[divisionNumericCode];

        if (
            division.isCountry &&
            populationDatabase.divisions[divisionNumericCode] &&
            (!emuDatabase || emuDatabase.divisions[divisionNumericCode])
        ) {
            result[divisionNumericCode] = division;
        }

        return result;
    }, {});

    return countries;
};

const getRegionIntersection = (databases, surveyDatabaseId, populationDatabaseId, emuDatabaseId) => {
    if (!surveyDatabaseId || !populationDatabaseId) {
        return [];
    }

    const surveyRegions = databases[surveyDatabaseId].regionCodes || [];

    const populationRegionSet = new Set(databases[populationDatabaseId].regionCodes || []);

    const emuRegionSet = emuDatabaseId && new Set(databases[emuDatabaseId].regionCodes || []);

    const regions = surveyRegions.reduce((result, region) => {
        if (populationRegionSet.has(region) && (!emuRegionSet || emuRegionSet.has(region))) {
            result.push(region);
        }

        return result;
    }, []);

    return regions;
};

const createRunTitle = (t, databases, selectedRegion, selectedCountryNumericCode, selectedSurveyDatabaseId, period) => {
    let countryAlphaCodeOrRegion;

    if (selectedRegion) {
        countryAlphaCodeOrRegion = selectedRegion;
    } else if (selectedCountryNumericCode === allRegionsId) {
        countryAlphaCodeOrRegion = "all regions";
    } else if (selectedCountryNumericCode) {
        countryAlphaCodeOrRegion = databases[selectedSurveyDatabaseId].divisions[selectedCountryNumericCode].alphaCode;
    }

    if (countryAlphaCodeOrRegion) {
        const { firstYear, lastYear } = period;

        return t("Defining run for {{countryAlphaCodeOrRegion}} during {{firstYear}}-{{lastYear}}", {
            countryAlphaCodeOrRegion,
            firstYear,
            lastYear,
        });
    }
};

const StartRunContainer = (props) => {
    const [state, setState] = React.useState(createInitialState());

    const signal = api.getSignal();

    const setStateSafely = (newState) => {
        setState((prevState) => ({
            ...prevState,
            ...newState,
        }));
    };

    React.useEffect(() => {
        setStateSafely({
            loadingData: true,
        });

        api.fetchDatabases(signal.token)
            .then((response) => {
                if (!response) {
                    return;
                }

                setStateSafely({
                    databases: normalizeDatabases(response.data),
                });
            })
            .finally(() => {
                setStateSafely({
                    loadingData: false,
                });
            });

        return () => {
            signal.cancel();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const countries = getCountryIntersection(
        state.databases,
        state.selectedSurveyDatabaseId,
        state.selectedPopulationDatabaseId,
        state.selectedEmuDatabaseId
    );

    const regions = getRegionIntersection(
        state.databases,
        state.selectedSurveyDatabaseId,
        state.selectedPopulationDatabaseId,
        state.selectedEmuDatabaseId
    );

    //==========================
    // Survey database
    //==========================

    const onChangeSurveyDatabaseType = (type) => {
        setStateSafely({
            surveyDatabaseType: type,
            selectedSurveyDatabaseId: undefined,
            selectedCountryNumericCode: undefined,
            selectedRegion: undefined,
        });
    };

    const onSelectSurveyDatabase = (databaseId) => {
        setStateSafely({
            selectedSurveyDatabaseId: databaseId,
            selectedCountryNumericCode: undefined,
            selectedRegion: undefined,
        });
    };

    const onSelectSurveyDatabaseFile = (file) => {
        setStateSafely({
            uploadedSurveyDatabaseFile: file,
        });
    };

    const onUploadSurveyDatabase = () => {
        const file = state.uploadedSurveyDatabaseFile;

        setStateSafely({
            uploadingSurveyDatabase: true,
        });

        uploadDatabase(file, "survey", onProgressSurveyDatabaseUpload, signal)
            .then((uploadDatabaseResponse) => {
                return api.fetchDatabases(signal.token).then((fetchDatabasesResponse) => {
                    const database = uploadDatabaseResponse.data;

                    setStateSafely({
                        databases: normalizeDatabases(fetchDatabasesResponse.data),
                        selectedSurveyDatabaseId: database.id,
                        surveyDatabaseType: "custom",
                    });
                });
            })
            .catch((error) => {
                const filename = state.uploadedSurveyDatabaseFile.name;

                showNotification("error", `Failed to upload "${filename}"`);

                throw error;
            })
            .finally(() => {
                setStateSafely({
                    uploadedSurveyDatabaseFile: undefined,
                    uploadingSurveyDatabase: false,
                });
            });
    };

    const onProgressSurveyDatabaseUpload = (progressEvent) => {
        setStateSafely({
            surveyDatabaseUploadProgress: (progressEvent.loaded / progressEvent.total) * 100,
        });
    };

    const onCancelSurveyDatabaseUpload = () => {
        setStateSafely({
            uploadedSurveyDatabaseFile: undefined,
        });
    };

    //==========================
    // Population database
    //==========================

    const onChangePopulationDatabaseType = (type) => {
        setStateSafely({
            populationDatabaseType: type,
            selectedPopulationDatabaseId: undefined,
            selectedCountryNumericCode: undefined,
            selectedRegion: undefined,
        });
    };

    const onSelectPopulationDatabase = (databaseId) => {
        setStateSafely({
            selectedPopulationDatabaseId: databaseId,
            selectedCountryNumericCode: undefined,
            selectedRegion: undefined,
        });
    };

    const onSelectPopulationDatabaseFile = (file) => {
        setStateSafely({
            uploadedPopulationDatabaseFile: file,
        });
    };

    const onUploadPopulationDatabase = () => {
        const file = state.uploadedPopulationDatabaseFile;

        setStateSafely({
            uploadingPopulationDatabase: true,
        });

        uploadDatabase(file, "population", onProgressPopulationDatabaseUpload, signal)
            .then((uploadDatabaseResponse) => {
                return api.fetchDatabases(signal.token).then((fetchDatabasesResponse) => {
                    const database = uploadDatabaseResponse.data;

                    setStateSafely({
                        databases: normalizeDatabases(fetchDatabasesResponse.data),
                        selectedPopulationDatabaseId: database.id,
                        populationDatabaseType: "custom",
                    });
                });
            })
            .catch((error) => {
                const filename = state.uploadedPopulationDatabaseFile.name;

                showNotification("error", `Failed to upload "${filename}"`);

                throw error;
            })
            .finally(() => {
                setStateSafely({
                    uploadedPopulationDatabaseFile: undefined,
                    uploadingPopulationDatabase: false,
                });
            });
    };

    const onProgressPopulationDatabaseUpload = (progressEvent) => {
        setStateSafely({
            populationDatabaseUploadProgress: (progressEvent.loaded / progressEvent.total) * 100,
        });
    };

    const onCancelPopulationDatabaseUpload = () => {
        setStateSafely({
            uploadedPopulationDatabaseFile: undefined,
        });
    };

    //==========================
    // EMU database
    //==========================

    const onChangeEmuDatabaseType = (type) => {
        setStateSafely({
            emuDatabaseType: type,
            selectedEmuDatabaseId: undefined,
            selectedCountryNumericCode: undefined,
            selectedRegion: undefined,
        });
    };

    const onSelectEmuDatabase = (databaseId) => {
        setStateSafely({
            selectedEmuDatabaseId: databaseId,
            selectedCountryNumericCode: undefined,
            selectedRegion: undefined,
        });
    };

    const onSelectEmuDatabaseFile = (file) => {
        setStateSafely({
            uploadedEmuDatabaseFile: file,
        });
    };

    const onUploadEmuDatabase = () => {
        const file = state.uploadedEmuDatabaseFile;

        setStateSafely({
            uploadingEmuDatabase: true,
        });

        uploadDatabase(file, "emu", onProgressEmuDatabaseUpload, signal)
            .then((uploadDatabaseResponse) => {
                return api.fetchDatabases(signal.token).then((fetchDatabasesResponse) => {
                    const database = uploadDatabaseResponse.data;

                    setStateSafely({
                        databases: normalizeDatabases(fetchDatabasesResponse.data),
                        selectedEmuDatabaseId: database.id,
                        emuDatabaseType: "custom",
                    });
                });
            })
            .catch((error) => {
                const filename = state.uploadedEmuDatabaseFile.name;

                showNotification("error", `Failed to upload "${filename}"`);

                throw error;
            })
            .finally(() => {
                setStateSafely({
                    uploadedEmuDatabaseFile: undefined,
                    uploadingEmuDatabase: false,
                });
            });
    };

    const onProgressEmuDatabaseUpload = (progressEvent) => {
        setStateSafely({
            emuDatabaseUploadProgress: (progressEvent.loaded / progressEvent.total) * 100,
        });
    };

    const onCancelEmuDatabaseUpload = () => {
        setStateSafely({
            uploadedEmuDatabaseFile: undefined,
        });
    };

    //==========================
    // Country
    //==========================

    const onSelectCountry = (countryNumericCode) => {
        setStateSafely({
            selectedCountryNumericCode: countryNumericCode,
            selectedRegion: undefined,
        });
    };

    //==========================
    // Run type
    //==========================

    const onSelectRunType = (runType) => {
        setStateSafely({
            selectedRunType: runType,
            selectedRegion: undefined,
        });
    };

    const onSelectRegion = (region) => {
        setStateSafely({
            selectedRegion: region,
        });
    };

    //==========================
    // Input settings
    //==========================

    const onRunNameChanged = (runName) => {
        setStateSafely({
            runName: runName,
        });
    };

    const onChangePeriod = (period) => {
        setStateSafely({
            period: period,
        });
    };

    //==========================
    // Buttons
    //==========================

    const onReviewData = () => {
        const countryNumericCodeOrRegion = state.selectedRegion || state.selectedCountryNumericCode.toString();

        setStateSafely({
            loadingTables: true,
        });

        // noinspection JSCheckFunctionSignatures
        Promise.all([
            api.fetchSurveyDatabase(
                state.selectedSurveyDatabaseId,
                countryNumericCodeOrRegion,
                undefined,
                state.selectedRunType,
                signal.token
            ),
            api.fetchPopulationDatabase(state.selectedPopulationDatabaseId, countryNumericCodeOrRegion, signal.token),
            state.selectedEmuDatabaseId
                ? api.fetchEmuDatabase(state.selectedEmuDatabaseId, countryNumericCodeOrRegion, signal.token)
                : new Promise((resolve) =>
                      resolve({
                          data: [],
                      })
                  ),
        ])
            .then((response) => {
                if (!response || response.some((item) => !item)) {
                    return;
                }

                const [surveyData, populationData, emuData] = response;

                setStateSafely({
                    surveyData: normalizeSurveyData(surveyData.data),
                    populationData: normalizePopulationData(populationData.data),
                    emuData: normalizeEmuData(emuData.data),
                });
            })
            .finally(() => {
                setStateSafely({
                    loadingTables: false,
                });
            });
    };

    const onStartRun = () => {
        setStateSafely({
            submittingRun: true,
        });

        let regionCodes;

        switch (state.selectedRunType) {
            case "National":
                regionCodes = [];
                break;

            case "Subnational":
                regionCodes = [state.selectedRegion];
                break;

            case "All":
                regionCodes = regions;
                break;

            default:
                break;
        }

        const params = {
            name: state.runName + DEBUG_BETA_STRING,
            surveyDatabaseId: state.selectedSurveyDatabaseId,
            populationDatabaseId: state.selectedPopulationDatabaseId,
            emuDatabaseId: state.selectedEmuDatabaseId,
            countryNumericCode: state.selectedCountryNumericCode,
            regionCode: state.selectedRegion,
            regionCodes: regionCodes,
            period: state.period,
            runType: state.selectedRunType,
            cancellationToken: undefined,
        };

        api.startRun(params).then(() => {
            setStateSafely({
                submittingRun: false,
            });
            props.history.push("/history");
        });
    };

    //==========================
    // Notification
    //==========================

    const showNotification = (variant, message) => {
        setStateSafely({
            notificationVariant: variant,
            notificationOpen: true,
            notificationMessage: message,
        });
    };

    const onCloseNotification = () => {
        setStateSafely({
            notificationOpen: false,
        });
    };

    //==========================
    // Render
    //==========================

    const firstYear = minimumYear;
    const lastYear = maximumYear;

    const surveyData = filterSurveyData(state.surveyData, firstYear, lastYear);

    const populationData = filterPopulationData(state.populationData, firstYear, lastYear);

    const runTitle = createRunTitle(
        props.t,
        state.databases,
        state.selectedRegion,
        state.selectedCountryNumericCode,
        state.selectedSurveyDatabaseId,
        state.period
    );

    return (
        <StartRun
            match={props.match}
            loadingData={state.loadingData}
            databases={state.databases}
            surveyDatabaseType={state.surveyDatabaseType}
            selectedSurveyDatabaseId={state.selectedSurveyDatabaseId}
            uploadedSurveyDatabaseName={getDatabaseName(state.uploadedSurveyDatabaseFile)}
            uploadingSurveyDatabase={state.uploadingSurveyDatabase}
            surveyDatabaseUploadProgress={state.surveyDatabaseUploadProgress}
            populationDatabaseType={state.populationDatabaseType}
            selectedPopulationDatabaseId={state.selectedPopulationDatabaseId}
            uploadedPopulationDatabaseName={getDatabaseName(state.uploadedPopulationDatabaseFile)}
            uploadingPopulationDatabase={state.uploadingPopulationDatabase}
            populationDatabaseUploadProgress={state.populationDatabaseUploadProgress}
            emuDatabaseType={state.emuDatabaseType}
            selectedEmuDatabaseId={state.selectedEmuDatabaseId}
            uploadedEmuDatabaseName={getDatabaseName(state.uploadedEmuDatabaseFile)}
            uploadingEmuDatabase={state.uploadingEmuDatabase}
            emuDatabaseUploadProgress={state.emuDatabaseUploadProgress}
            countries={countries}
            selectedCountryNumericCode={state.selectedCountryNumericCode}
            regions={regions}
            selectedRegion={state.selectedRegion}
            runName={state.runName}
            period={state.period}
            runTitle={runTitle}
            loadingTables={state.loadingTables}
            surveyData={surveyData}
            populationData={populationData}
            emuData={state.emuData}
            notificationVariant={state.notificationVariant}
            notificationOpen={state.notificationOpen}
            notificationMessage={state.notificationMessage}
            onChangeSurveyDatabaseType={onChangeSurveyDatabaseType}
            onSelectSurveyDatabase={onSelectSurveyDatabase}
            onSelectSurveyDatabaseFile={onSelectSurveyDatabaseFile}
            onUploadSurveyDatabase={onUploadSurveyDatabase}
            onCancelSurveyDatabaseUpload={onCancelSurveyDatabaseUpload}
            onChangePopulationDatabaseType={onChangePopulationDatabaseType}
            onSelectPopulationDatabase={onSelectPopulationDatabase}
            onSelectPopulationDatabaseFile={onSelectPopulationDatabaseFile}
            onUploadPopulationDatabase={onUploadPopulationDatabase}
            onCancelPopulationDatabaseUpload={onCancelPopulationDatabaseUpload}
            onChangeEmuDatabaseType={onChangeEmuDatabaseType}
            onSelectEmuDatabase={onSelectEmuDatabase}
            onSelectEmuDatabaseFile={onSelectEmuDatabaseFile}
            onUploadEmuDatabase={onUploadEmuDatabase}
            onCancelEmuDatabaseUpload={onCancelEmuDatabaseUpload}
            onSelectCountry={onSelectCountry}
            onSelectRegion={onSelectRegion}
            onRunNameChanged={onRunNameChanged}
            onChangePeriod={onChangePeriod}
            onReviewData={onReviewData}
            onStartRun={onStartRun}
            onCloseNotification={onCloseNotification}
            selectedRunType={state.selectedRunType}
            onSelectRunType={onSelectRunType}
            submittingRun={state.submittingRun}
        />
    );
};

export default withTranslation()(withErrorBoundary(StartRunContainer));
